<script lang="ts">
  import type { Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    SCHEDULING_ACTION_TOOLTIP_CLASS,
    schedulingActionTooltips,
  } from "../../../../../../../../../../../_shared/_logic/action-tooltips"
  import StepActionCTA from "../../../../../../../../../_shared/_modules/step-action-cta"
  import type {
    AnnouncementPlanActionIntent,
    AnnouncementPlanActionIntents,
    AnnouncementPlanActionPhase,
    AnnouncementPlanActionResult,
    AnnouncementPlanOperations,
  } from "../../actions/announcementPlans"

  type Props = {
    children?: Snippet<[Snippet, Snippet]>
    intents: AnnouncementPlanActionIntents
    operations: AnnouncementPlanOperations
    onSubmissionResult: (result: AnnouncementPlanActionResult) => Promise<void> | void
    onPhaseChange?: (phase: AnnouncementPlanActionPhase) => void
  }

  const {
    children = null,
    intents,
    operations,
    onSubmissionResult,
    onPhaseChange = () => {},
  }: Props = $props()

  let pendingKind = $state<"cancel" | "complete" | "none" | null>(null)

  const submitResult = async (
    output: AnnouncementPlanActionResult["output"],
    committed: boolean,
  ) => {
    try {
      await onSubmissionResult({ kind: "committed", output })
    } catch (error) {
      console.error("announcement_plan_completion_failed", error)
      newNotification({
        class: "error",
        html: committed
          ? "Änderung gespeichert, Oberfläche konnte nicht aktualisiert werden."
          : "Der Schritt konnte nicht fortgesetzt werden.",
      })
    }
  }

  const run = async (intent: AnnouncementPlanActionIntent) => {
    if (pendingKind) return

    pendingKind = intent.kind

    if (intent.kind === "none") onPhaseChange("continuing")
    else if (intent.kind === "cancel") onPhaseChange("canceling")
    else if (intent.kind === "complete") onPhaseChange("completing")

    try {
      if (intent.kind === "none") {
        await submitResult(
          { action: "none", canceled: [], completed: [], skipped: [], failed: [] },
          false,
        )

        return
      }

      if (intent.kind === "cancel") {
        const response = await operations.cancel(intent.items)
        await submitResult({ action: "canceled", completed: [], ...response }, true)

        return
      }

      const response = await operations.complete(intent.items)
      await submitResult({ action: "completed", canceled: [], ...response }, true)
    } catch (error) {
      const completing = intent.kind === "complete"
      console.error(
        completing ? "complete_announcement_plans_failed" : "cancel_announcement_plans_failed",
        error,
      )
      newNotification({
        class: "error",
        html: completing
          ? "Ankündigungspläne konnten nicht abgeschlossen werden."
          : "Ankündigungspläne konnten nicht abgebrochen werden.",
      })
    } finally {
      pendingKind = null
      onPhaseChange("idle")
    }
  }
</script>

{#snippet actionButton(intent: AnnouncementPlanActionIntent)}
  <StepActionCTA
    type={intent.buttonType}
    content={pendingKind === intent.kind
      ? intent.kind === "complete"
        ? "Ankündigungspläne werden abgeschlossen…"
        : intent.kind === "cancel"
          ? "Ankündigungspläne werden abgebrochen…"
          : "Wird fortgesetzt…"
      : intent.label}
    tooltipContent={intent.kind === "cancel"
      ? schedulingActionTooltips.cancelReplanCancelAnnouncements
      : intent.kind === "complete"
        ? schedulingActionTooltips.cancelReplanCompleteAnnouncements
        : undefined}
    tooltipClassName={intent.kind === "none" ? undefined : SCHEDULING_ACTION_TOOLTIP_CLASS}
    disabled={Boolean(pendingKind)}
    disabledReason={pendingKind ? "Ankündigungspläne werden gerade bearbeitet." : null}
    onClick={() => run(intent)}
  />
{/snippet}

{#snippet primaryAction()}
  <div class="announcement-plan-actions">
    {#each intents.filter((intent) => intent.buttonType === "primaryButton") as intent (intent.kind)}
      {@render actionButton(intent)}
    {/each}
  </div>
{/snippet}

{#snippet footerActions()}
  <div class="announcement-plan-actions">
    {#each intents as intent (intent.kind)}
      {@render actionButton(intent)}
    {/each}
  </div>
{/snippet}

{#if children}
  {@render children(primaryAction, footerActions)}
{:else}
  {@render footerActions()}
{/if}

<style>
  .announcement-plan-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
  }
</style>
